import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import "../../styles/Edit.css";
import { updateUserDepartment, fetchDepartmentOptions } from '../../utils/api';
import { BIOBANK_ROUTES } from '../../constants/routes';

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "3px",
    marginLeft: "10px",
    minHeight: "36px",
    textAlign: "left",
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": { borderColor: "#ccc" },
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: "transparent",
    color: "black",
    cursor: "pointer",
    padding: 0,
    margin: 0,
  }),
  menu: (provided) => ({
    ...provided,
    position: "absolute",
    zIndex: 999,
    marginBottom: "5px",
    scrollBehavior: "auto",
    overflowAnchor: "none",
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    marginTop: "0px",
    marginLeft: "10px",
    width: "calc(100% - 10px)",
    textAlign: "left",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: "310px",
    overflowY: "auto",
    padding: 0,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "rgba(80, 0, 0, 0.1)",
    borderRadius: "3px",
    margin: "1px 2px",
    padding: "1px 4px",
    display: "flex",
    alignItems: "center",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "black",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "var(--tamu-maroon)",
    cursor: "default",
    "&:hover": {
      backgroundColor: "rgba(80, 0, 0, 0.2)",
      color: "var(--tamu-maroon)",
    },
  }),
};

const Option = (props) => {
  return (
    <div
      {...props.innerProps}
      className="custom-option"
      style={{
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        backgroundColor: props.isSelected
          ? "rgba(80, 0, 0, 0.1)"
          : "transparent",
        cursor: "pointer",
      }}
    >
      <div
        className="checkbox"
        style={{
          width: "16px",
          height: "16px",
          border: "1px solid var(--tamu-maroon)",
          borderRadius: "3px",
          marginRight: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: props.isSelected
            ? "var(--tamu-maroon)"
            : "transparent",
        }}
      >
        {props.isSelected && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )}
      </div>
      {props.label}
    </div>
  );
};

const EditDepartment = ({ userDepartments = [], onSave }) => {
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [otherDept, setOtherDept] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const loadDepartmentOptions = async () => {
      try {
        const departments = await fetchDepartmentOptions();
        const options = departments.map((dept) => ({
          value: dept,
          label: dept,
        }));
        if (!options.some((opt) => opt.value === "Other")) {
          options.push({ value: "Other", label: "Other" });
        }
        setDepartmentOptions(options);
      } catch (error) {
        console.error("Error loading department options:", error);
        setError("Failed to load departments. Please try again.");
      }
    };
    loadDepartmentOptions();
  }, []);

  useEffect(() => {
    if (departmentOptions.length > 0 && userDepartments.length > 0) {
      setSelectedDepartments(
        departmentOptions.filter((option) =>
          userDepartments.includes(option.value)
        )
      );
    }
  }, [departmentOptions, userDepartments]);

  const handleDepartmentChange = (selectedOptions) => {
    setSelectedDepartments(selectedOptions || []);
  };

  const handleOtherChange = (e) => {
    setOtherDept(e.target.value);
  };

  const validate = () => {
    if (selectedDepartments.length === 0) {
      setError("You must select at least one department.");
      return false;
    }

if (
  selectedDepartments.some((dept) => dept.value === "Other")
) {
  if (!otherDept || otherDept.trim() === "") {
    setError("Please specify the department name.");
    return false;
  }
}

setError("");
return true;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      if (onSave) {
        onSave({
          departments: selectedDepartments.map((d) => d.value),
          otherDept,
        });
      } else {
        try {
          await updateUserDepartment(selectedDepartments, otherDept);
          navigate(BIOBANK_ROUTES.PROFILE);
        } catch (error) {
          console.error("Error saving departments:", error);
          setError("Failed to save department changes. Please try again.");
        }
      }
    }
  };

  const handleCancel = () => {
    navigate(BIOBANK_ROUTES.PROFILE);
  };

  return (
    <div className="edit-wrapper">
      <div className="edit-container">
        <button className="close-btn" onClick={handleCancel}>
          X
        </button>
        <div className="edit-header">
          <h2>Edit Department</h2>
        </div>
        {error && <p className="error-message">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="edit-content">
            <div className="department-select">
              <label htmlFor="department">Department</label>
              <Select
                id="department"
                options={departmentOptions}
                isMulti
                value={selectedDepartments}
                onChange={handleDepartmentChange}
                className={`department-select ${error ? "error-border" : ""}`}
                components={{ Option }}
                styles={customSelectStyles}
              />
            </div>
            {selectedDepartments.some((dept) => dept.value === "Other") && (
              <div className="other-field">
                <label htmlFor="otherDept">Specify Other</label>
                <input
                  type="text"
                  id="otherDept"
                  value={otherDept}
                  onChange={handleOtherChange}
                  placeholder="Enter department"
                />
              </div>
            )}
          </div>
          <div className="button-group">
            <button type="submit" className="save-btn">
              Save
            </button>
            <button type="button" className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDepartment;